import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Building2, Search, Coins, ArrowRight } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { t } from "../lib/i18n";

export default function RoleSelect() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(null);

  const choose = async (role) => {
    setSubmitting(role);
    try {
      await api.post("/users/role", { role });
      await refresh();
      toast.success(t("Rol confirmado"));
      if (role === "seller") navigate("/seller/confidentiality");
      else if (role === "searcher") navigate("/searcher/onboarding");
      else navigate("/investor/onboarding");
    } catch (e) {
      toast.error(t("Error guardando rol"));
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16">
      <div className="max-w-6xl w-full">
        <div className="text-center space-y-3 mb-14">
          <span className="font-mono-label text-gold">{t("Paso final · Selección de rol")}</span>
          <h1 className="font-serif-display text-4xl md:text-5xl">
            {t("¿Cómo vas a usar Relevo,")}{" "}{user?.name?.split(" ")[0] || t("tú")}?
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            {t("Elige tu rol principal. Podrás solicitar cambio más adelante.")}
          </p>
        </div>

        <div className="grid md:grid-cols-3 hairline border-r-0 border-b-0">
          <RoleCard
            testid="role-select-seller"
            icon={<Building2 className="w-6 h-6" />}
            num="01"
            title={t("Soy Vendedor")}
            desc={t("Tengo una empresa o gestiono una, y estoy explorando una venta o transmisión.")}
            bullets={["Cuestionario progresivo en 3 etapas", "Teaser ciego auto-generado por IA", "Visibilidad solo bajo tu permiso"]}
            onClick={() => choose("seller")}
            loading={submitting === "seller"}
          />
          <RoleCard
            testid="role-select-searcher"
            icon={<Search className="w-6 h-6" />}
            num="02"
            title={t("Soy Searcher")}
            desc={t("Estoy buscando una empresa para adquirir y operar (search fund).")}
            bullets={["Marketplace de empresas pre-cualificadas", "Pipeline visual de tu deal flow", "Scoring inteligente con IA"]}
            onClick={() => choose("searcher")}
            loading={submitting === "searcher"}
          />
          <RoleCard
            testid="role-select-investor"
            icon={<Coins className="w-6 h-6" />}
            num="03"
            title={t("Soy Inversor")}
            desc={t("Aporto capital (equity, deuda o mixto) a operaciones de search funds.")}
            bullets={["Oportunidades filtradas por tu perfil", "Términos anonimizados antes de NDA", "Equity, deuda o mixto"]}
            onClick={() => choose("investor")}
            loading={submitting === "investor"}
          />
        </div>
      </div>
    </div>
  );
}

const RoleCard = ({ icon, num, title, desc, bullets, onClick, loading, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={loading}
    data-testid={testid}
    className="text-left hairline border-l-0 border-t-0 p-8 md:p-10 bg-card hover:bg-bone transition-colors group disabled:opacity-50"
  >
    <div className="flex items-start justify-between mb-8">
      <div className="w-11 h-11 hairline flex items-center justify-center bg-[#1E3A5F] text-bone">
        {icon}
      </div>
      <span className="font-serif-display text-3xl text-gold">{num}</span>
    </div>
    <h2 className="font-serif-display text-2xl mb-2">{title}</h2>
    <p className="text-sm text-muted-foreground mb-6">{desc}</p>
    <ul className="space-y-2 text-sm mb-8">
      {bullets.map((b, i) => (
        <li key={i} className="flex items-start gap-2">
          <span className="text-gold mt-1">·</span> {b}
        </li>
      ))}
    </ul>
    <span className="font-mono-label flex items-center gap-2 text-[#1E3A5F] group-hover:gap-3 transition-all">
      {loading ? t("Guardando…") : t("Continuar")} <ArrowRight className="w-4 h-4" />
    </span>
  </button>
);
